package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.common.PageInfoDto
import co.maytes.standupbot.shared.dto.model.ModelDto
import co.maytes.standupbot.shared.dto.standuprun.RunDraftsDto
import co.maytes.standupbot.shared.dto.standuprun.SectionEditRequestDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunAckDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunFilters
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.dto.standuprun.UserSectionDto
import co.maytes.standupbot.shared.dto.promptarchive.PromptArchiveDto
import io.ktor.client.request.parameter

data class StandupFilterState(
    val standupId: String? = null,
    val status: StandupRunStatusDto? = null,
    val scheduledAfterIso: String? = null,
    val scheduledBeforeIso: String? = null,
) {
    val isEmpty: Boolean
        get() = standupId == null && status == null && scheduledAfterIso == null && scheduledBeforeIso == null
}

sealed interface ListState {
    data object Idle : ListState
    data object Loading : ListState
    data object LoadingMore : ListState
    data class Error(val error: ApiError) : ListState
    data object Loaded : ListState
}

class StandupRunsStore(private val api: ApiClient) {
    var items: List<StandupRunSummaryDto> by mutableStateOf(emptyList())
        private set

    var pageInfo: PageInfoDto? by mutableStateOf(null)
        private set

    var listState: ListState by mutableStateOf(ListState.Idle)
        private set

    var filters: StandupFilterState by mutableStateOf(StandupFilterState())
        private set

    private val detailCache = mutableStateMapOf<String, StandupRunDetailDto>()
    private var detailInFlight = mutableStateMapOf<String, Boolean>()
    var detailError: ApiError? by mutableStateOf(null)
        private set

    fun setFilters(next: StandupFilterState) {
        filters = next
    }

    fun cachedDetail(runId: String): StandupRunDetailDto? = detailCache[runId]

    fun isFetchingDetail(runId: String): Boolean = detailInFlight[runId] == true

    suspend fun list(workspaceId: String) {
        listState = ListState.Loading
        val filterDto = filters.toDto(cursor = null)
        val res = fetch(workspaceId, filterDto)
        when (res) {
            is Result.Ok -> {
                items = res.value.items
                pageInfo = res.value.pageInfo
                listState = ListState.Loaded
            }
            is Result.Err -> {
                listState = ListState.Error(res.error)
            }
        }
    }

    suspend fun loadMore(workspaceId: String) {
        val cursor = pageInfo?.nextCursor ?: return
        if (listState == ListState.LoadingMore) return
        listState = ListState.LoadingMore
        val res = fetch(workspaceId, filters.toDto(cursor = cursor))
        when (res) {
            is Result.Ok -> {
                items = items + res.value.items
                pageInfo = res.value.pageInfo
                listState = ListState.Loaded
            }
            is Result.Err -> {
                listState = ListState.Error(res.error)
            }
        }
    }

    suspend fun listByStandup(
        workspaceId: String,
        standupId: String,
        limit: Int = 5,
    ): Result<List<StandupRunSummaryDto>, ApiError> {
        val filterDto = StandupRunFilters(standupId = standupId, limit = limit)
        return when (val res = fetch(workspaceId, filterDto)) {
            is Result.Ok -> Result.Ok(res.value.items)
            is Result.Err -> Result.Err(res.error)
        }
    }

    suspend fun detail(workspaceId: String, runId: String): Result<StandupRunDetailDto, ApiError> {
        detailCache[runId]?.let { return Result.Ok(it) }
        detailInFlight[runId] = true
        val res = api.get<StandupRunDetailDto>(
            path = "/api/v1/workspaces/$workspaceId/runs/$runId",
        )
        detailInFlight[runId] = false
        when (res) {
            is Result.Ok -> {
                detailCache[runId] = res.value
                detailError = null
            }
            is Result.Err -> {
                detailError = res.error
            }
        }
        return res
    }

    suspend fun ackStandup(
        workspaceId: String,
        runId: String,
    ): Result<StandupRunAckDto, ApiError> {
        val res = api.post<StandupRunAckDto>("/api/v1/workspaces/$workspaceId/runs/$runId/ack")
        if (res is Result.Ok) refreshDetail(workspaceId, runId)
        return res
    }

    suspend fun editSection(
        workspaceId: String,
        runId: String,
        userId: String,
        yesterday: String,
        today: String,
        blockers: String?,
    ): Result<UserSectionDto, ApiError> {
        val res = api.post<SectionEditRequestDto, UserSectionDto>(
            path = "/api/v1/workspaces/$workspaceId/runs/$runId/sections/$userId",
            body = SectionEditRequestDto(yesterday = yesterday, today = today, blockers = blockers),
        )
        if (res is Result.Ok) refreshDetail(workspaceId, runId)
        return res
    }

    suspend fun skipSection(
        workspaceId: String,
        runId: String,
        userId: String,
    ): Result<UserSectionDto, ApiError> {
        val res = api.post<UserSectionDto>(
            "/api/v1/workspaces/$workspaceId/runs/$runId/sections/$userId/skip"
        )
        if (res is Result.Ok) refreshDetail(workspaceId, runId)
        return res
    }

    suspend fun revertSection(
        workspaceId: String,
        runId: String,
        userId: String,
    ): Result<UserSectionDto, ApiError> {
        val res = api.post<UserSectionDto>(
            "/api/v1/workspaces/$workspaceId/runs/$runId/sections/$userId/revert"
        )
        if (res is Result.Ok) refreshDetail(workspaceId, runId)
        return res
    }

    suspend fun fetchPromptArchive(
        workspaceId: String,
        runId: String,
    ): Result<List<PromptArchiveDto>, ApiError> =
        api.get<List<PromptArchiveDto>>(
            path = "/api/v1/workspaces/$workspaceId/runs/$runId/prompt-archive",
        )

    suspend fun fetchDrafts(
        workspaceId: String,
        runId: String,
    ): Result<RunDraftsDto, ApiError> =
        api.get<RunDraftsDto>(path = "/api/v1/workspaces/$workspaceId/runs/$runId/drafts")

    suspend fun fetchModels(): Result<List<ModelDto>, ApiError> =
        api.get<List<ModelDto>>(path = "/api/v1/models")

    private suspend fun refreshDetail(workspaceId: String, runId: String) {
        detailCache.remove(runId)
        detail(workspaceId, runId)
    }

    fun invalidateDetail(runId: String) {
        detailCache.remove(runId)
    }

    fun invalidateAll() {
        detailCache.clear()
        items = emptyList()
        pageInfo = null
        listState = ListState.Idle
    }

    private suspend fun fetch(
        workspaceId: String,
        filters: StandupRunFilters,
    ): Result<PagedResponse<StandupRunSummaryDto>, ApiError> {
        return api.get<PagedResponse<StandupRunSummaryDto>>(
            path = "/api/v1/workspaces/$workspaceId/runs",
        ) {
            filters.toQueryParams().forEach { (k, v) -> parameter(k, v) }
        }
    }
}

private fun StandupFilterState.toDto(cursor: String?): StandupRunFilters = StandupRunFilters(
    standupId = standupId,
    status = status,
    scheduledAfter = scheduledAfterIso?.let { runCatching { kotlinx.datetime.Instant.parse(it) }.getOrNull() },
    scheduledBefore = scheduledBeforeIso?.let { runCatching { kotlinx.datetime.Instant.parse(it) }.getOrNull() },
    limit = StandupRunFilters.DEFAULT_LIMIT,
    cursor = cursor,
)

@Composable
fun rememberStandupRunsStore(): StandupRunsStore {
    val api = rememberApiClient()
    return remember(api) { StandupRunsStore(api) }
}
